import {
    VStack,
    Text,
    Flex,
    Spinner,
    Center,
    HStack,
    Button,
    Icon,
    Box,
    Avatar,
    Badge,
} from '@chakra-ui/react';
import { useState, useMemo, useEffect } from 'react';
import { TbMessageCircle, TbAt } from 'react-icons/tb';
import { useGetNotifications, NotificationItem } from '../../hooks/useGetNotifications';
import { useOpenNotificationTarget } from '../../hooks/useOpenNotificationTarget';
import { useMarkNotificationRead } from '../../hooks/useMarkNotificationRead';
import { useMarkAllNotificationsRead } from '../../hooks/useMarkAllNotificationsRead';
import AppDrawer from '../AppDrawer';

interface MessageDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

const formatRelativeTime = (date: Date): string => {
    const now = new Date();
    const d = new Date(date);
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const MessageRow = ({ notification, onClick, onVehicleClick }: {
    notification: NotificationItem;
    onClick: () => void;
    onVehicleClick: (e: React.MouseEvent) => void;
}) => {
    const senderName = notification.senderName ?? 'Someone';
    const isExplicit = notification.explicit === true;
    const vehicleLabel = notification.preview
        ? `${notification.preview.vehicle ?? ''}${notification.preview.roNumber ? ` · RO ${notification.preview.roNumber}` : ''}`.trim()
        : '';

    return (
        <Box
            px={4}
            py={3}
            cursor="pointer"
            bg={notification.read ? 'white' : 'blue.50'}
            borderBottomWidth="1px"
            borderColor="gray.100"
            _hover={{ bg: notification.read ? 'gray.50' : 'blue.100' }}
            onClick={onClick}
            transition="background-color 150ms ease"
        >
            <Flex gap={3} align="start">
                {isExplicit ? (
                    <Flex
                        w="36px"
                        h="36px"
                        borderRadius="full"
                        bg="blue.50"
                        align="center"
                        justify="center"
                        flexShrink={0}
                    >
                        <Icon as={TbAt as React.ElementType} w={4} h={4} color="blue.500" />
                    </Flex>
                ) : (
                    <Avatar
                        size="sm"
                        name={senderName}
                        bg="#0071E3"
                        color="white"
                        fontSize="xs"
                        fontWeight="semibold"
                        flexShrink={0}
                    />
                )}
                <Box flex={1} minW={0}>
                    <HStack spacing={2} align="center">
                        <Text
                            fontWeight={notification.read ? 'normal' : 'semibold'}
                            fontSize="sm"
                            color="gray.800"
                        >
                            {isExplicit
                                ? <>{senderName} mentioned you</>
                                : <>{senderName} left a message</>
                            }
                        </Text>
                        {isExplicit && (
                            <Badge
                                fontSize="9px"
                                px={1.5}
                                py={0.5}
                                borderRadius="md"
                                bg="blue.100"
                                color="blue.700"
                                fontWeight="medium"
                                textTransform="none"
                            >
                                Mention
                            </Badge>
                        )}
                        {/* Yaxle employees get tagged from shops they aren't in; opening switches there. */}
                        {notification.otherOrganization && (
                            <Badge
                                fontSize="9px"
                                px={1.5}
                                py={0.5}
                                borderRadius="md"
                                bg="bg.muted"
                                color="fg.muted"
                                fontWeight="medium"
                                textTransform="none"
                                maxW="140px"
                                noOfLines={1}
                            >
                                {notification.otherOrganization.name}
                            </Badge>
                        )}
                    </HStack>
                    {vehicleLabel && (
                        <Text
                            fontSize="xs"
                            color="gray.500"
                            mt={0.5}
                            noOfLines={1}
                            _hover={{ textDecoration: 'underline', color: '#0071E3' }}
                            onClick={onVehicleClick}
                        >
                            {vehicleLabel}
                        </Text>
                    )}
                    <Text fontSize="xs" color="gray.400" mt={1}>
                        {formatRelativeTime(notification.timeCreated)}
                    </Text>
                </Box>

                {!notification.read && (
                    <HStack spacing={0} flexShrink={0} align="center" h="36px">
                        <Box w="8px" h="8px" borderRadius="full" bg="blue.500" />
                    </HStack>
                )}
            </Flex>
        </Box>
    );
};

const MessageDrawer = ({ isOpen, onClose }: MessageDrawerProps) => {
    const openNotificationTarget = useOpenNotificationTarget();
    const markAsRead = useMarkNotificationRead();
    const [offset, setOffset] = useState(0);
    const [allNotifications, setAllNotifications] = useState<NotificationItem[]>([]);

    const markAllAsRead = useMarkAllNotificationsRead(() => {
        setAllNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }, 'order-mention');

    useEffect(() => {
        if (isOpen) {
            setOffset(0);
        }
    }, [isOpen]);

    const { data, isLoading, isFetching } = useGetNotifications(isOpen, undefined, offset, 'order-mention');

    useEffect(() => {
        if (isOpen && data?.notifications) {
            if (offset === 0) {
                setAllNotifications(data.notifications);
            } else {
                setAllNotifications(prev => [...prev, ...data.notifications]);
            }
        }
    }, [isOpen, data?.notifications, offset]);

    const unreadCount = useMemo(
        () => allNotifications.filter(n => !n.read).length,
        [allNotifications],
    );

    const handleClick = (notification: NotificationItem) => {
        if (!notification.read) {
            markAsRead.mutate({ notificationId: notification._id });
        }
        if (notification.data.type === 'order') {
            openNotificationTarget(notification, `/order/${notification.data.orderId}`, {
                source: 'messages',
                openChat: true,
            });
            onClose();
        }
    };

    const handleVehicleClick = (notification: NotificationItem, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!notification.read) {
            markAsRead.mutate({ notificationId: notification._id });
        }
        if (notification.data.type === 'order') {
            openNotificationTarget(notification, `/order/${notification.data.orderId}`);
            onClose();
        }
    };

    return (
        <AppDrawer
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            title="Messages"
            titleAddon={<Icon as={TbMessageCircle as React.ElementType} w={5} h={5} color="#0071E3" />}
            contentProps={{ overflow: 'hidden' }}
            bodyProps={{ p: 0, pt: 0, px: 0, py: 0 }}
            headerRight={
                unreadCount > 0 ? (
                    <Button
                        size="xs"
                        variant="solid"
                        bg="#0071E3"
                        color="white"
                        fontWeight="normal"
                        px={2}
                        _hover={{ bg: '#005BC4' }}
                        onClick={() => markAllAsRead.mutate()}
                        isLoading={markAllAsRead.isPending}
                    >
                        Mark {unreadCount} as read
                    </Button>
                ) : undefined
            }
        >
            {isLoading && offset === 0 ? (
                <Center h="200px">
                    <Spinner color="#0071E3" size="lg" />
                </Center>
            ) : allNotifications.length === 0 ? (
                <Center h="200px">
                    <Flex direction="column" align="center" gap={2}>
                        <Icon as={TbMessageCircle as React.ElementType} w={8} h={8} color="gray.300" />
                        <Text color="gray.500" fontSize="sm">
                            No messages yet
                        </Text>
                    </Flex>
                </Center>
            ) : (
                <VStack spacing={0} align="stretch">
                    {allNotifications.map(notification => (
                        <MessageRow
                            key={notification._id}
                            notification={notification}
                            onClick={() => handleClick(notification)}
                            onVehicleClick={(e) => handleVehicleClick(notification, e)}
                        />
                    ))}
                    {data?.hasMore && (
                        <Box p={4} borderTopWidth="1px" borderColor="gray.100">
                            <Button
                                w="100%"
                                size="sm"
                                variant="outline"
                                borderColor="#0071E3"
                                color="#0071E3"
                                _hover={{ bg: '#0071E308' }}
                                onClick={() => setOffset(prev => prev + 20)}
                                isLoading={isFetching && offset > 0}
                            >
                                View More
                            </Button>
                        </Box>
                    )}
                </VStack>
            )}
        </AppDrawer>
    );
};

export default MessageDrawer;
